// src/components/profile/SingleFieldEditModal.tsx
import React, { useState, useEffect } from 'react';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   TextField,
   Button,
   Box,
   Typography,
   IconButton,
   CircularProgress,
   Avatar,
   Chip,
   Stack,
   Card,
   Grid,
} from '@mui/material';
import {
   Close as CloseIcon,
   Person as PersonIcon,
   Language as LanguageIcon,
   Description as DescriptionIcon,
   PhotoCamera as PhotoCameraIcon,
   Add as AddIcon,
   Business as BusinessIcon,
   Public as WebsiteIcon,
   Phone as PhoneIcon,
   Label as TaglineIcon,
   Delete as DeleteIcon,
} from '@mui/icons-material';
import { db, storage, auth } from '../../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import GooglePlacesSearch from '../common/GooglePlacesSearch';

interface WhatIDoCard {
   id: string;
   title: string;
   description: string;
}

interface LookingForItem {
   id: string;
   title: string;
   description: string;
}

interface FeaturedProject {
   id: string;
   title: string;
   description: string;
}

interface Interest {
   id: string;
   emoji: string;
   name: string;
   selected: boolean;
   isCustom?: boolean;
}

interface SingleFieldEditModalProps {
   open: boolean;
   onClose: () => void;
   userId: string;
   fieldType: 'displayName' | 'photoURL' | 'bio' | 'languages' | 'tagline' | 'companyName' | 'website' | 'phoneNumber' | 'location' | 'whatIDo' | 'lookingFor' | 'featuredProjects' | 'interests';
   currentValue?: string | string[] | any[];
   onSave: () => void;
}

const SingleFieldEditModal: React.FC<SingleFieldEditModalProps> = ({
   open,
   onClose,
   userId,
   fieldType,
   currentValue,
   onSave,
}) => {
   const navigate = useNavigate();
   const [value, setValue] = useState<string>('');
   const [languages, setLanguages] = useState<string[]>([]);
   const [newLanguage, setNewLanguage] = useState('');
   const [uploading, setUploading] = useState(false);
   const [saving, setSaving] = useState(false);
   const [previewImage, setPreviewImage] = useState<string | null>(null);
   const [selectedFile, setSelectedFile] = useState<File | null>(null);
   
   // Complex field states
   const [whatIDo, setWhatIDo] = useState<WhatIDoCard[]>([]);
   const [lookingFor, setLookingFor] = useState<LookingForItem[]>([]);
   const [featuredProjects, setFeaturedProjects] = useState<FeaturedProject[]>([]);
   const [interests, setInterests] = useState<Interest[]>([]);
   
   // Form states for adding new items
   const [showAddForm, setShowAddForm] = useState(false);
   const [newItemTitle, setNewItemTitle] = useState('');
   const [newItemDescription, setNewItemDescription] = useState('');
   
   // Predefined interests
   const predefinedInterests: Interest[] = [
      { id: 'ai', emoji: '🤖', name: 'AI & Machine Learning', selected: false },
      { id: 'startup', emoji: '🚀', name: 'Startup Ecosystems', selected: false },
      { id: 'dev', emoji: '💻', name: 'Software Development', selected: false },
      { id: 'design', emoji: '📱', name: 'Product Design', selected: false },
      { id: 'data', emoji: '📊', name: 'Data Science', selected: false },
      { id: 'innovation', emoji: '💡', name: 'Innovation Strategy', selected: false },
      { id: 'creative', emoji: '🎨', name: 'Creative Technology', selected: false },
      { id: 'api', emoji: '🌐', name: 'API Development', selected: false },
   ];
   
   // Check if this is a complex field
   const isComplexField = ['whatIDo', 'lookingFor', 'featuredProjects', 'interests'].includes(fieldType);

   useEffect(() => {
      if (open) {
         if (fieldType === 'languages' && Array.isArray(currentValue)) {
            setLanguages(currentValue as string[]);
         } else if (fieldType === 'whatIDo' && Array.isArray(currentValue)) {
            setWhatIDo(currentValue as WhatIDoCard[]);
         } else if (fieldType === 'lookingFor' && Array.isArray(currentValue)) {
            setLookingFor(currentValue as LookingForItem[]);
         } else if (fieldType === 'featuredProjects' && Array.isArray(currentValue)) {
            setFeaturedProjects(currentValue as FeaturedProject[]);
         } else if (fieldType === 'interests' && Array.isArray(currentValue)) {
            setInterests(currentValue as Interest[]);
         } else if (typeof currentValue === 'string') {
            setValue(currentValue);
            if (fieldType === 'photoURL') {
               setPreviewImage(currentValue);
            }
         }
      }
   }, [open, currentValue, fieldType]);

   const getFieldInfo = () => {
      switch (fieldType) {
         case 'displayName':
            return {
               title: 'Add Your Name',
               icon: <PersonIcon />,
               placeholder: 'Enter your full name',
               label: 'Display Name',
               multiline: false,
            };
         case 'photoURL':
            return {
               title: 'Add Profile Photo',
               icon: <PhotoCameraIcon />,
               placeholder: '',
               label: 'Profile Photo',
               multiline: false,
            };
         case 'bio':
            return {
               title: 'Add Your Bio',
               icon: <DescriptionIcon />,
               placeholder: 'Tell others about yourself...',
               label: 'Bio',
               multiline: true,
            };
         case 'languages':
            return {
               title: 'Add Languages',
               icon: <LanguageIcon />,
               placeholder: 'Enter a language',
               label: 'Languages',
               multiline: false,
            };
         case 'tagline':
            return {
               title: 'Add Your Tagline',
               icon: <TaglineIcon />,
               placeholder: 'A catchy tagline or slogan',
               label: 'Tagline',
               multiline: false,
            };
         case 'companyName':
            return {
               title: 'Add Company Name',
               icon: <BusinessIcon />,
               placeholder: 'Enter your company or organization',
               label: 'Company',
               multiline: false,
            };
         case 'website':
            return {
               title: 'Add Your Website',
               icon: <WebsiteIcon />,
               placeholder: 'https://example.com',
               label: 'Website',
               multiline: false,
            };
         case 'phoneNumber':
            return {
               title: 'Add Phone Number',
               icon: <PhoneIcon />,
               placeholder: '+1 (555) 123-4567',
               label: 'Phone Number',
               multiline: false,
            };
         case 'location':
            return {
               title: 'Add Your Location',
               icon: <PersonIcon />,
               placeholder: 'City, Country',
               label: 'Location',
               multiline: false,
            };
         case 'whatIDo':
            return {
               title: 'Add What You Do',
               icon: <PersonIcon />,
               placeholder: '',
               label: 'What I Do',
               multiline: false,
            };
         case 'lookingFor':
            return {
               title: 'Add What You\'re Looking For',
               icon: <PersonIcon />,
               placeholder: '',
               label: 'Looking For',
               multiline: false,
            };
         case 'featuredProjects':
            return {
               title: 'Add Featured Projects',
               icon: <PersonIcon />,
               placeholder: '',
               label: 'Featured Projects',
               multiline: false,
            };
         case 'interests':
            return {
               title: 'Add Interests & Expertise',
               icon: <PersonIcon />,
               placeholder: '',
               label: 'Interests & Expertise',
               multiline: false,
            };
         default:
            return {
               title: 'Edit Field',
               icon: null,
               placeholder: '',
               label: '',
               multiline: false,
            };
      }
   };

   const fieldInfo = getFieldInfo();

   const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
         setSelectedFile(file);
         const reader = new FileReader();
         reader.onloadend = () => {
            setPreviewImage(reader.result as string);
         };
         reader.readAsDataURL(file);
      }
   };

   const handleAddLanguage = () => {
      if (newLanguage.trim() && !languages.includes(newLanguage.trim())) {
         setLanguages([...languages, newLanguage.trim()]);
         setNewLanguage('');
      }
   };

   const handleRemoveLanguage = (lang: string) => {
      setLanguages(languages.filter((l) => l !== lang));
   };

   const handleSave = async () => {
      setSaving(true);
      try {
         let updateData: any = {};

         if (fieldType === 'photoURL' && selectedFile) {
            // Upload image to Firebase Storage
            setUploading(true);
            const storageRef = ref(storage, `profile_photos/${userId}/${Date.now()}_${selectedFile.name}`);
            await uploadBytes(storageRef, selectedFile);
            const downloadURL = await getDownloadURL(storageRef);
            updateData.photoURL = downloadURL;
            setUploading(false);
         } else if (fieldType === 'languages') {
            updateData.languages = languages;
         } else if (fieldType === 'displayName') {
            updateData.displayName = value.trim();
         } else if (fieldType === 'bio') {
            updateData.bio = value.trim();
         } else if (fieldType === 'tagline') {
            updateData.tagline = value.trim();
         } else if (fieldType === 'companyName') {
            updateData.companyName = value.trim();
         } else if (fieldType === 'website') {
            updateData.website = value.trim();
         } else if (fieldType === 'phoneNumber') {
            updateData.phoneNumber = value.trim();
         } else if (fieldType === 'location') {
            updateData.location = value.trim();
         } else if (fieldType === 'whatIDo') {
            updateData.whatIDo = whatIDo;
         } else if (fieldType === 'lookingFor') {
            updateData.lookingFor = lookingFor;
         } else if (fieldType === 'featuredProjects') {
            updateData.featuredProjects = featuredProjects;
         } else if (fieldType === 'interests') {
            updateData.interests = interests;
         }

         // Call backend API to update profile and sync across all collections
         const token = await auth.currentUser?.getIdToken();
         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/profile/${userId}`, {
            method: 'PUT',
            headers: {
               'Content-Type': 'application/json',
               Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(updateData),
         });

         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || errorData.message || 'Failed to update profile');
         }

         const result = await response.json();
         console.log('✅ Profile field updated successfully:', result.message);

         onSave();
         handleClose();
      } catch (error) {
         console.error('Error saving field:', error);
         alert(error instanceof Error ? error.message : 'Failed to save. Please try again.');
      } finally {
         setSaving(false);
      }
   };

   const handleClose = () => {
      setValue('');
      setLanguages([]);
      setNewLanguage('');
      setPreviewImage(null);
      setSelectedFile(null);
      setWhatIDo([]);
      setLookingFor([]);
      setFeaturedProjects([]);
      setInterests([]);
      setShowAddForm(false);
      setNewItemTitle('');
      setNewItemDescription('');
      onClose();
   };

   const isValid = () => {
      if (fieldType === 'photoURL') {
         return selectedFile !== null || previewImage !== null;
      }
      if (fieldType === 'languages') {
         return languages.length > 0;
      }
      if (fieldType === 'whatIDo') {
         return whatIDo.length > 0;
      }
      if (fieldType === 'lookingFor') {
         return lookingFor.length > 0;
      }
      if (fieldType === 'featuredProjects') {
         return featuredProjects.length > 0;
      }
      if (fieldType === 'interests') {
         return interests.filter(i => i.selected).length > 0;
      }
      return value.trim().length > 0;
   };

   // Handlers for complex fields
   const handleAddItem = () => {
      if (!newItemTitle.trim()) return;
      
      const newItem = {
         id: Date.now().toString(),
         title: newItemTitle.trim(),
         description: newItemDescription.trim(),
      };
      
      if (fieldType === 'whatIDo') {
         setWhatIDo([...whatIDo, newItem]);
      } else if (fieldType === 'lookingFor') {
         setLookingFor([...lookingFor, newItem]);
      } else if (fieldType === 'featuredProjects') {
         setFeaturedProjects([...featuredProjects, newItem]);
      }
      
      setNewItemTitle('');
      setNewItemDescription('');
      setShowAddForm(false);
   };

   const handleRemoveItem = (id: string) => {
      if (fieldType === 'whatIDo') {
         setWhatIDo(whatIDo.filter(item => item.id !== id));
      } else if (fieldType === 'lookingFor') {
         setLookingFor(lookingFor.filter(item => item.id !== id));
      } else if (fieldType === 'featuredProjects') {
         setFeaturedProjects(featuredProjects.filter(item => item.id !== id));
      }
   };

   const toggleInterest = (interestId: string) => {
      setInterests((prev) => {
         const existing = prev.find((i) => i.id === interestId);
         if (existing) {
            return prev.map((interest) =>
               interest.id === interestId ? { ...interest, selected: !interest.selected } : interest
            );
         } else {
            const predefined = predefinedInterests.find((i) => i.id === interestId);
            if (predefined) {
               return [...prev, { ...predefined, selected: true }];
            }
            return prev;
         }
      });
   };

   const addCustomInterest = () => {
      if (interests.length < 6) {
         const newInterest: Interest = {
            id: Date.now().toString(),
            emoji: '⭐',
            name: '',
            selected: true,
            isCustom: true,
         };
         setInterests([...interests, newInterest]);
      }
   };

   const updateCustomInterest = (id: string, field: keyof Interest, value: string) => {
      setInterests((prev) =>
         prev.map((interest) => (interest.id === id ? { ...interest, [field]: value } : interest))
      );
   };

   const removeCustomInterest = (id: string) => {
      setInterests((prev) => prev.filter((interest) => interest.id !== id));
   };

   const getMaxItems = () => {
      if (fieldType === 'whatIDo') return 4;
      if (fieldType === 'lookingFor') return 5;
      if (fieldType === 'featuredProjects') return 3;
      if (fieldType === 'interests') return 6;
      return 0;
   };

   const getCurrentItems = () => {
      if (fieldType === 'whatIDo') return whatIDo;
      if (fieldType === 'lookingFor') return lookingFor;
      if (fieldType === 'featuredProjects') return featuredProjects;
      return [];
   };

   return (
      <Dialog 
         open={open} 
         onClose={handleClose} 
         maxWidth="sm"
         fullWidth
         PaperProps={{
            sx: {
               borderRadius: 2,
            },
         }}
      >
         <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar
                     sx={{
                        bgcolor: 'primary.light',
                        color: 'primary.main',
                        width: 40,
                        height: 40,
                     }}
                  >
                     {fieldInfo.icon}
                  </Avatar>
                  <Typography variant="h6" fontWeight="600">
                     {fieldInfo.title}
                  </Typography>
               </Box>
               <IconButton onClick={handleClose} size="small">
                  <CloseIcon />
               </IconButton>
            </Box>
         </DialogTitle>

         <DialogContent>
            {fieldType === 'whatIDo' || fieldType === 'lookingFor' || fieldType === 'featuredProjects' ? (
               <Box>
                  {/* Add Form */}
                  {showAddForm && (
                     <Box
                        sx={{
                           p: 2,
                           border: '2px dashed #00C4A7',
                           borderRadius: 1.5,
                           mb: 2,
                        }}
                     >
                        <Typography variant='body2' fontWeight={500} color='#00C4A7' sx={{ mb: 2 }}>
                           Add {fieldInfo.label}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                           <TextField
                              fullWidth
                              size='small'
                              placeholder={`Title (e.g., ${fieldType === 'whatIDo' ? 'Product Manager' : fieldType === 'lookingFor' ? 'Co-founder' : 'Website Redesign'})`}
                              value={newItemTitle}
                              onChange={(e) => setNewItemTitle(e.target.value)}
                              autoFocus
                           />
                           <TextField
                              fullWidth
                              size='small'
                              multiline
                              rows={2}
                              placeholder='Brief description...'
                              value={newItemDescription}
                              onChange={(e) => setNewItemDescription(e.target.value)}
                           />
                           <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              <Button size='small' onClick={() => setShowAddForm(false)} sx={{ color: '#6b7280' }}>
                                 Cancel
                              </Button>
                              <Button
                                 size='small'
                                 variant='contained'
                                 onClick={handleAddItem}
                                 disabled={!newItemTitle.trim()}
                                 sx={{
                                    background: 'linear-gradient(135deg, #00C4A7 0%, #004369 100%)',
                                    '&:hover': {
                                       background: 'linear-gradient(135deg, #007075 0%, #002A42 100%)',
                                    },
                                 }}
                              >
                                 Add
                              </Button>
                           </Box>
                        </Box>
                     </Box>
                  )}

                  {/* Existing Items */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                     {getCurrentItems().map((item: any) => (
                        <Box
                           key={item.id}
                           sx={{
                              p: 2,
                              border: '1px solid #e2e8f0',
                              borderRadius: 1.5,
                              position: 'relative',
                           }}
                        >
                           <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box sx={{ flex: 1, pr: 1 }}>
                                 <Typography variant='body1' fontWeight={500} color='#1f2937' sx={{ mb: 0.5 }}>
                                    {item.title}
                                 </Typography>
                                 {item.description && (
                                    <Typography variant='body2' color='#6b7280'>
                                       {item.description}
                                    </Typography>
                                 )}
                              </Box>
                              <IconButton
                                 onClick={() => handleRemoveItem(item.id)}
                                 size='small'
                                 sx={{
                                    width: 32,
                                    height: 32,
                                    background: '#fee2e2',
                                    color: '#dc2626',
                                    borderRadius: 1,
                                    '&:hover': {
                                       background: '#fecaca',
                                    },
                                 }}
                              >
                                 <DeleteIcon fontSize='small' />
                              </IconButton>
                           </Box>
                        </Box>
                     ))}
                  </Box>

                  {/* Add Button */}
                  {getCurrentItems().length < getMaxItems() && !showAddForm && (
                     <Button
                        fullWidth
                        variant='outlined'
                        onClick={() => setShowAddForm(true)}
                        sx={{
                           border: '1px dashed #d1d5db',
                           borderRadius: 1.5,
                           color: '#00C4A7',
                           fontWeight: 500,
                           py: 1,
                           '&:hover': {
                              background: '#f3f4f6',
                              borderColor: '#00C4A7',
                           },
                        }}
                     >
                        + Add {fieldInfo.label} ({getCurrentItems().length}/{getMaxItems()})
                     </Button>
                  )}
               </Box>
            ) : fieldType === 'interests' ? (
               <Box>
                  <Typography variant='body2' color='#6b7280' sx={{ mb: 2 }}>
                     Select up to 6 interests (tap to select/deselect)
                  </Typography>

                  <Grid container spacing={1.5} sx={{ mb: 2 }}>
                     {predefinedInterests.map((interest) => {
                        const isSelected = interests.find((i) => i.id === interest.id)?.selected || false;
                        return (
                           <Grid item xs={6} key={interest.id}>
                              <Card
                                 onClick={() => toggleInterest(interest.id)}
                                 sx={{
                                    p: 2,
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    background: isSelected
                                       ? 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)'
                                       : '#f9fafb',
                                    border: isSelected ? '2px solid #00C4A7' : '2px solid #e5e7eb',
                                    borderRadius: 1.5,
                                    '&:hover': {
                                       borderColor: '#00C4A7',
                                    },
                                 }}
                              >
                                 <Typography sx={{ fontSize: 28, mb: 0.5 }}>
                                    {interest.emoji}
                                 </Typography>
                                 <Typography variant='body2' fontWeight={600} color='#374151' sx={{ fontSize: '0.75rem' }}>
                                    {interest.name}
                                 </Typography>
                              </Card>
                           </Grid>
                        );
                     })}
                  </Grid>

                  {/* Custom Interests */}
                  {interests
                     .filter((interest) => interest.isCustom)
                     .map((interest) => (
                        <Box key={interest.id} sx={{ mb: 2 }}>
                           <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                              <TextField
                                 fullWidth
                                 size='small'
                                 placeholder='Custom interest (e.g., Photography, Cooking)'
                                 value={interest.name}
                                 onChange={(e) => updateCustomInterest(interest.id, 'name', e.target.value)}
                              />
                              <IconButton
                                 onClick={() => removeCustomInterest(interest.id)}
                                 sx={{
                                    width: 36,
                                    height: 36,
                                    background: '#fee2e2',
                                    color: '#dc2626',
                                    borderRadius: 1.5,
                                    '&:hover': {
                                       background: '#fecaca',
                                    },
                                 }}
                              >
                                 <DeleteIcon />
                              </IconButton>
                           </Box>
                        </Box>
                     ))}

                  {interests.length < 6 && (
                     <Button
                        fullWidth
                        variant='outlined'
                        onClick={addCustomInterest}
                        sx={{
                           border: '1px dashed #d1d5db',
                           borderRadius: 1.5,
                           color: '#00C4A7',
                           fontWeight: 500,
                           py: 1,
                           '&:hover': {
                              background: '#f3f4f6',
                              borderColor: '#00C4A7',
                           },
                        }}
                     >
                        + Add Custom Interest ({interests.length}/6)
                     </Button>
                  )}
               </Box>
            ) : fieldType === 'photoURL' ? (
               <Box sx={{ textAlign: 'center', py: 2 }}>
                  {previewImage && (
                     <Avatar
                        src={previewImage}
                        sx={{
                           width: 120,
                           height: 120,
                           margin: '0 auto 20px',
                           border: '3px solid',
                           borderColor: 'primary.main',
                        }}
                     />
                  )}
                  <Button
                     variant="outlined"
                     component="label"
                     startIcon={<PhotoCameraIcon />}
                     fullWidth
                  >
                     {previewImage ? 'Change Photo' : 'Upload Photo'}
                     <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleFileSelect}
                     />
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                     Recommended: Square image, at least 400x400px
                  </Typography>
               </Box>
            ) : fieldType === 'languages' ? (
               <Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                     <TextField
                        fullWidth
                        size="small"
                        placeholder={fieldInfo.placeholder}
                        value={newLanguage}
                        onChange={(e) => setNewLanguage(e.target.value)}
                        onKeyPress={(e) => {
                           if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddLanguage();
                           }
                        }}
                     />
                     <Button
                        variant="contained"
                        onClick={handleAddLanguage}
                        disabled={!newLanguage.trim()}
                        startIcon={<AddIcon />}
                     >
                        Add
                     </Button>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                     {languages.map((lang) => (
                        <Chip
                           key={lang}
                           label={lang}
                           onDelete={() => handleRemoveLanguage(lang)}
                           color="primary"
                           variant="outlined"
                        />
                     ))}
                  </Stack>
                  {languages.length === 0 && (
                     <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                        No languages added yet
                     </Typography>
                  )}
               </Box>
            ) : fieldType === 'location' ? (
               <Box sx={{ mt: 1 }}>
                  <GooglePlacesSearch
                     value={value}
                     searchType='places'
                     label='Location'
                     placeholder='City, State, Country'
                     onPlaceSelect={(place_name: string, lat?: number, lng?: number) => {
                        setValue(place_name);
                     }}
                     sx={{
                        '& .MuiOutlinedInput-root': {
                           borderRadius: 1.5,
                           '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#00C4A7',
                           },
                           '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#00C4A7',
                           },
                        },
                     }}
                  />
               </Box>
            ) : (
               <TextField
                  fullWidth
                  label={fieldInfo.label}
                  placeholder={fieldInfo.placeholder}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  multiline={fieldInfo.multiline}
                  rows={fieldInfo.multiline ? 4 : 1}
                  autoFocus
                  sx={{ mt: 1 }}
               />
            )}
         </DialogContent>

         <DialogActions sx={{ p: 2.5, pt: 0 }}>
            <Button onClick={handleClose} disabled={saving || uploading}>
               Cancel
            </Button>
            <Button
               variant="contained"
               onClick={handleSave}
               disabled={!isValid() || saving || uploading}
               startIcon={saving || uploading ? <CircularProgress size={20} /> : null}
               sx={{
                  background: 'linear-gradient(135deg, #00C4A7 0%, #004369 100%)',
                  '&:hover': {
                     background: 'linear-gradient(135deg, #007075 0%, #002A42 100%)',
                  },
               }}
            >
               {saving || uploading ? 'Saving...' : 'Save'}
            </Button>
         </DialogActions>
      </Dialog>
   );
};

export default SingleFieldEditModal;

